import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Bird, Cross, Star, Feather, Send } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const RugaciuniPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Rugăciuni și Cereri de Rugăciune - Cuvânt și Rugăciune",
    "description": "Împărtășește-ți nevoile și cererile de rugăciune cu comunitatea Cuvânt și Rugăciune. Suntem o comunitate care se roagă împreună și se sprijină în credință.",
    "url": "https://cuvantsirugaciune.ro/rugaciuni",
    "mainEntity": {
      "@type": "Service",
      "name": "Serviciul de Rugăciune",
      "description": "Serviciu de primire și procesare a cererilor de rugăciune",
      "provider": {
        "@type": "Organization",
        "name": "Cuvânt și Rugăciune",
        "founder": {
          "@type": "Person",
          "name": "Mircea Stanciu"
        }
      },
      "areaServed": {
        "@type": "Country",
        "name": "România"
      }
    }
  };

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      <SEOHead 
        title="Rugăciuni și Cereri de Rugăciune - Cuvânt și Rugăciune cu Mircea Stanciu"
        description="Împărtășește-ți nevoile și cererile de rugăciune cu comunitatea Cuvânt și Rugăciune. Suntem o comunitate creștină care se roagă împreună și se sprijină în credință."
        keywords="rugaciuni crestine, cereri rugaciune, rugaciune comunitara, rugaciune crestina romania, intercesiune, rugaciune pentru vindecre, rugaciune pentru familie"
        canonicalUrl="/rugaciuni"
        ogTitle="Rugăciuni și Cereri - Comunitatea Creștină Cuvânt și Rugăciune"
        ogDescription="Alătură-te comunității noastre în rugăciune. Împarte-ți nevoile și găsește sprijin spiritual în credința comună."
        structuredData={structuredData}
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1504052434569-70ad5836ab65')`
          }}
        />
        
        {/* Decorative dove pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-8 gap-16 p-8">
            {[...Array(32)].map((_, i) => (
              <Bird key={i} className="w-6 h-6 text-amber-400" />
            ))}
          </div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
              <Cross className="w-10 h-10 sm:w-16 sm:h-16 text-amber-400" />
              <Feather className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent leading-tight">
              Rugăciuni
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 leading-relaxed px-4"
            >
              Împărtășește-ți nevoile și cererile de rugăciune cu comunitatea noastră
            </motion.p>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-base sm:text-lg text-amber-400 mb-6 sm:mb-8 font-medium"
            >
              "Rugați-vă neîncetat" - 1 Tesaloniceni 5:17
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Prayer Request Form Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
                <Feather className="w-6 h-6 sm:w-8 sm:h-8 text-amber-300" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-amber-400">Cereri de Rugăciune</h2>
              <p className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed">
                Împărtășește-ți nevoile și cererile de rugăciune. Suntem o comunitate care se roagă 
                împreună și se sprijină în credință.
              </p>
              
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const message = formData.get('message');
                  const nume = formData.get('nume');
                  const prenume = formData.get('prenume');
                  const email = formData.get('email');
                  
                  // Mostra stato di caricamento
                  const submitBtn = e.target.querySelector('button[type="submit"]');
                  const originalText = submitBtn.innerHTML;
                  submitBtn.innerHTML = '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Invio in corso...';
                  submitBtn.disabled = true;
                  
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mailgun/prayer-request`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ 
                        name: nume || 'Vizitator anonim',
                        message: message,
                        email: email,
                        nume: nume,
                        prenume: prenume
                      }),
                    });

                    const result = await response.json();
                    
                    if (response.ok && result.success) {
                      // Messaggio di successo migliorato
                      const successMessage = result.sandbox_mode 
                        ? '✅ Cererea ta de rugăciune a fost primită! În versiunea finală, vei primi o confirmare prin email. Dumnezeu te binecuvânteze! 🙏'
                        : (result.message || 'Cererea ta de rugăciune a fost trimisă cu succes! Dumnezeu te binecuvânteze! 🙏');
                      
                      alert(successMessage);
                      e.target.reset();
                    } else {
                      // Messaggio di errore migliorato
                      const errorMessage = result.detail?.includes('sandbox') || result.message?.includes('sandbox')
                        ? '📝 Cererea ta a fost înregistrată! Momentan sistemul este în modul de testare, dar mesajul tău a fost salvat. Mulțumim pentru răbdare!'
                        : 'A apărut o problemă tehnică. Te rugăm să încerci din nou peste câteva minute.';
                      
                      alert(errorMessage);
                    }
                  } catch (error) {
                    console.error('Prayer request error:', error);
                    alert('📡 Problemă de conexiune. Te rugăm să încerci din nou peste câteva momente.');
                  } finally {
                    // Ripristina il pulsante
                    submitBtn.innerHTML = originalText;
                    submitBtn.disabled = false;
                  }
                }}
                className="space-y-4 sm:space-y-6"
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-amber-400 font-semibold mb-2 text-sm">
                      Nume (opțional):
                    </label>
                    <input
                      name="nume"
                      type="text"
                      placeholder="Numele tău"
                      className="w-full p-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-amber-400 font-semibold mb-2 text-sm">
                      Prenume (opțional):
                    </label>
                    <input
                      name="prenume"
                      type="text"
                      placeholder="Prenumele tău"
                      className="w-full p-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-amber-400 font-semibold mb-2 text-sm">
                    Email (opțional):
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="email@exemplu.com"
                    className="w-full p-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-amber-400 font-semibold mb-2 flex items-center space-x-2">
                    <Cross className="w-4 h-4" />
                    <span>Cererea ta de rugăciune:</span>
                  </label>
              
                  <textarea
                    name="message"
                    rows="4"
                    className="w-full p-3 sm:p-4 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                    placeholder="Împărtășește-ți cererea de rugăciune aici..."
                    required
                  />
                </div>
              
                <button
                  type="submit"
                  className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Trimite Cererea</span>
                </button>
              </form>

            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-1 lg:order-2"
            >
              <img 
                src="https://images.unsplash.com/photo-1504052434569-70ad5836ab65"
                alt="Rugăciune și Meditație Creștină"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent rounded-3xl"></div>
              <div className="absolute top-4 right-4">
                <Bird className="w-8 h-8 text-amber-400" />
              </div>
              <div className="absolute bottom-4 left-4">
                <Cross className="w-6 h-6 text-amber-400" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prayer Types Section */}
      <section className="py-16 sm:py-20 bg-slate-800/50 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400 mb-4">Tipuri de Rugăciuni</h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Ne rugăm pentru diverse nevoi și situații ale vieții
            </p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: Heart,
                title: "Vindecări",
                description: "Rugăciuni pentru vindecarea trupească și sufletească"
              },
              {
                icon: Star,
                title: "Familii",
                description: "Rugăciuni pentru armonía și unitatea familiilor"
              },
              {
                icon: Cross,
                title: "Mântuire",
                description: "Rugăciuni pentru salvarea celor dragi"
              },
              {
                icon: Bird,
                title: "Pace",
                description: "Rugăciuni pentru pace în suflet și în lume"
              },
              {
                icon: Feather,
                title: "Îndrumări",
                description: "Rugăciuni pentru îndrumarea divină în decizii"
              },
              {
                icon: Send,
                title: "Mulțumiri",
                description: "Rugăciuni de recunoștință pentru binecuvântări"
              }
            ].map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-slate-700/50 rounded-2xl border border-amber-400/20"
              >
                <type.icon className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-amber-400 mb-3">{type.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default RugaciuniPage;